'use client';

import {
  Article,
  Button,
  Code,
  H1,
  H2,
  Hr,
  Link,
  Nav,
  P,
  Small,
  Span,
  Strong,
  cn,
} from '@/components/ui';
import { FooterUI, Navbar } from '@/components/widget';
import { httpStatusCopyFor } from '@/lib/httpStatusCopy';

export type HttpStatusScreenProps = {
  statusCode: number;
  /** Overrides default title for this code */
  title?: string;
  /** Overrides default description */
  description?: string;
  homeHref?: string;
  homeLabel?: string;
  /** Next.js opaque digest — safe to display; helps support correlate logs. */
  digest?: string;
  /** Pre-sanitized single-line hint (e.g. dev-only); never pass raw stack traces. */
  technicalHint?: string;
  resetLabel?: string;
  onReset?: () => void;
  className?: string;
};

export default function HttpStatusScreen({
  statusCode,
  title,
  description,
  homeHref = '/',
  homeLabel = 'Back to home',
  digest,
  technicalHint,
  resetLabel = 'Try again',
  onReset,
  className,
}: HttpStatusScreenProps) {
  const copy = httpStatusCopyFor(statusCode);
  const heading = title ?? copy.title;
  const body = description ?? copy.description;
  const pageLabel = `${statusCode} ${heading}`;

  return (
    <div className={cn('http-status-page', 'home-grid', 'relative')}>
      <Navbar />
      <main
        className={cn(
          'http-status-main',
          'home-main',
          'flex min-h-[50vh] flex-1 flex-col items-center justify-center px-4 py-12 text-[var(--color-fg)] md:py-16',
          className,
        )}
        role="main"
      >
        <H1 className="sr-only">{pageLabel}</H1>
        <Article
          className="article w-full max-w-lg rounded-2xl border border-[color-mix(in_oklab,var(--color-fg)_12%,transparent)] bg-[color-mix(in_oklab,var(--color-surface)_96%,transparent)] p-8 shadow-sm"
          aria-labelledby="http-status-heading"
        >
        <P className="p mb-2 text-center text-sm font-medium uppercase tracking-wide text-[color-mix(in_oklab,var(--color-fg)_55%,transparent)]">
          <Span aria-hidden>HTTP </Span>
          <Strong className="font-semibold text-[var(--color-fg)]">{statusCode}</Strong>
        </P>
        <H2 id="http-status-heading" className="h2 mb-4 text-center text-2xl font-semibold">
          {heading}
        </H2>
        <P className="p mb-6 text-center text-[color-mix(in_oklab,var(--color-fg)_88%,transparent)]">{body}</P>

        {technicalHint ? (
          <P className="p mb-6 rounded-md bg-[color-mix(in_oklab,var(--color-fg)_6%,transparent)] p-3 text-left text-sm">
            <Code className="code block whitespace-pre-wrap break-words text-left" title="Technical detail">
              {technicalHint}
            </Code>
          </P>
        ) : null}

        {digest ? (
          <P className="p mb-6 text-center">
            <Small className="small text-[color-mix(in_oklab,var(--color-fg)_55%,transparent)]">
              Reference ID: <Code className="code">{digest}</Code>
            </Small>
          </P>
        ) : null}

        <Hr className="hr mb-6" />

        <Nav aria-label="Recovery actions" className="nav flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link href={homeHref} className="btn anchor text-center" btn aria-label={homeLabel}>
            {homeLabel}
          </Link>
          {onReset ? (
            <Button type="button" className="btn" onClick={onReset}>
              {resetLabel}
            </Button>
          ) : null}
        </Nav>

        <div className="mt-8 text-center" role="note">
          <Small className="small text-[color-mix(in_oklab,var(--color-fg)_45%,transparent)]">
            For your security, detailed errors are not shown on this page.
          </Small>
        </div>
      </Article>
      </main>
      <FooterUI />
    </div>
  );
}
